import { callTool, callToolEnvelope } from './tools'
import {
  grantArgs,
  literalGlob,
  parseAccessRequest,
  parseDirectoryAccess,
  parseDirectoryNetworkIds,
  parseNetworkUpdate,
  parseNodeAccessList,
  parseRevoke,
  parseRoster,
  parseShareList,
  splitEnvelope,
  type AccessLevel,
  type DirectoryNodeAccess,
  type NetworkAccess,
  type NodeAccessView,
  type NodeSetting,
  type OrgMemberLike,
  type ShareList,
} from '../utilities/nodeAccess'

/**
 * The router's tools for who may use a node (router docs/PERMISSIONS.md § 4.1, router#1757, app#375).
 * Every read and write goes through `callTool`: `nodes.access`, `networks.access` and the list take
 * no app writes, so there is no Supabase path to fall back on.
 *
 * ⚠ Each call throws a `ToolCallError`; the caller shows its message. None of these is a money
 * surface, and the router writes its refusals for a person to read.
 */

/** The node's setting, whether it is shared now, its entries and its pending requests. Controllers only. */
export const readNodeAccess = async (nodeId: string): Promise<NodeAccessView> => {
  // ⚠ The payload is under `node_access`, not the tool's name, so `callTool` hands back the whole
  // envelope, with the router's notes beside the payload. That is what the panel needs.
  const body = await callTool('node_access_list', { node_id: nodeId })
  const view = parseNodeAccessList(body)
  if (!view) throw new Error('The router sent an access answer this page cannot read.')
  return view
}

/** Sets the node to follow its network, or to Private (you and org admins). Returns the router's notes. */
export const setNodeAccess = async (nodeId: string, access: NodeSetting): Promise<string[]> =>
  splitEnvelope('node_access_set', await callToolEnvelope('node_access_set', { node_id: nodeId, access })).notes

/**
 * Sets the NETWORK's access, Org or Private (§ 4.1, D-20): network owner or Admin. The answer names
 * every node the change opened (Private → Org) or closed (Org → Private).
 */
export const setNetworkAccess = async (networkId: string, access: NetworkAccess) => {
  const { payload, notes } = splitEnvelope(
    'network_update',
    await callToolEnvelope('network_update', { network_id: networkId, access })
  )
  return { ...parseNetworkUpdate(payload), notes }
}

/**
 * Adds a member at `use` or `root`. ⚠ `confirm_root` is sent only for root, and only after the
 * reader confirmed the plain warning: the router refuses a root grant without it (D-3).
 */
export const grantNodeAccess = async (
  nodeId: string,
  user: string,
  level: AccessLevel,
  confirmRoot = false
): Promise<string[]> => {
  const args = grantArgs(nodeId, user, level, confirmRoot)
  return splitEnvelope('node_access_grant', await callToolEnvelope('node_access_grant', args)).notes
}

/** Removes a member's entry. The answer names every terminal that still lets them visit (D-4). */
export const revokeNodeAccess = async (nodeId: string, user: string) =>
  parseRevoke(await callTool('node_access_revoke', { node_id: nodeId, user }))

/**
 * What the reader holds on the nodes of one network, or on one node by name
 * (`network_directory`'s `my_access`, router#1757). Every member may read it; `node_access_list` is
 * for controllers only. `brief` leaves out the services, which this read does not need.
 */
export const readMyAccess = async (filter: {
  networkName?: string
  nodeName?: string
}): Promise<{ networkIds: Set<string>; nodes: DirectoryNodeAccess[] }> => {
  const body = await callTool('network_directory', {
    brief: true,
    ...(filter.networkName ? { network_name: filter.networkName } : {}),
    ...(filter.nodeName ? { node_name_glob: literalGlob(filter.nodeName) } : {}),
  })
  return { networkIds: parseDirectoryNetworkIds(body), nodes: parseDirectoryAccess(body) }
}

/** Asks for use of one node. */
export const requestNodeAccess = async (nodeId: string) =>
  parseAccessRequest(await callTool('node_access_request', { node_id: nodeId }))

/** Asks for use of every node of a network: one request per node. */
export const requestNetworkAccess = async (networkId: string) =>
  parseAccessRequest(await callTool('node_access_request', { all_nodes: true, network_id: networkId }))

/** Grants (a use entry) or refuses a pending request, in one step. */
export const decideNodeAccess = async (requestId: string, decision: 'grant' | 'refuse'): Promise<string[]> =>
  splitEnvelope('node_access_decide', await callToolEnvelope('node_access_decide', { request_id: requestId, decision }))
    .notes

/** Removes one address or pattern from a service's visitor list (the node's controllers). */
export const removeServiceVisitor = async (serviceId: string, entry: string): Promise<void> => {
  await callTool('service_share', { service_id: serviceId, action: 'remove', emails: [entry] })
}

/** Closes a service to "all org members": only its named visitors and the node's controllers stay. */
export const closeServiceToOrg = async (serviceId: string): Promise<void> => {
  await callTool('service_update', { service_id: serviceId, all_org_members: false })
}

/**
 * Reads a service's visitor list from the router (`service_share` action `list`): the addresses,
 * each named when it is a member of the node's org (router#1841), and "all org members". Null when
 * the answer has no list this page can read.
 */
export const readServiceVisitors = async (serviceId: string): Promise<ShareList | null> =>
  parseShareList(await callTool('service_share', { service_id: serviceId, action: 'list' }))

/**
 * The roster of the node's org (`org_members_list`), for the grant form. Null when the reader may
 * not list it (an Owner or Admin only) or the read failed: the form then takes an email.
 *
 * ⚠ A read of its own, never the shared `useOrgMembers` store: a refused read leaves that store
 * holding the last org the reader listed, which is not the node's org.
 */
export const readOrgRoster = async (orgId: string): Promise<OrgMemberLike[] | null> => {
  try {
    return parseRoster(await callTool('org_members_list', { org_id: orgId }))
  } catch (e) {
    console.warn('org_members_list', orgId, e)
    return null
  }
}
